import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { ApiUsersmemfaProvidersFormatGetCollectionResponse } from "@/generated/api";
import { apiUsersmemfaProvidersFormatGetCollectionOptions } from "@/generated/api/@tanstack/react-query.gen.ts";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import LoadingAnimate from "@/components/loading-animate";
import { MfaMethod } from "@/app/(guest)/setting-mfa/_data/setting-mfa-schema.ts";
import { TwoFactorFooter } from "@/app/(guest)/two-factor/_components/two-factor-footer.tsx";
import { TwoFactorForm } from "@/app/(guest)/two-factor/_components/two-factor-form.tsx";
import { TwoFactorMethodSelector } from "@/app/(guest)/two-factor/_components/two-factor-method-selector.tsx";
import {
  getCurrentMfaMethod,
  getMfaMethodsAvailable,
} from "@/app/(guest)/two-factor/_data/mfa-constant.ts";

export type TMfaMethodList = {
  mfaMethod: ApiUsersmemfaProvidersFormatGetCollectionResponse["member"][0]["provider"];
  isCurrent: ApiUsersmemfaProvidersFormatGetCollectionResponse["member"][0]["current"];
};

const searchSchema = z.object({
  callbackUrl: z.string().optional(),
});

export const Route = createFileRoute("/(guest)/two-factor/")({
  component: RouteComponent,
  validateSearch: (search) => searchSchema.parse(search),
});

function RouteComponent() {
  const [mfaMethod, setMfaMethod] = useState<MfaMethod>();
  const [mfaMethodsAvailable, setMfaMethodsAvailable] = useState<
    TMfaMethodList["mfaMethod"][]
  >([]);

  const {
    data: mfaProviders,
    isLoading,
    error,
  } = useQuery({
    ...apiUsersmemfaProvidersFormatGetCollectionOptions({}),
    refetchOnWindowFocus: false,
    select: (data): TMfaMethodList[] => {
      return data.member.map((item) => {
        return { mfaMethod: item.provider, isCurrent: item.current };
      });
    },
  });

  useEffect(() => {
    if (mfaProviders && !isLoading) {
      const currentMfaMethod = getCurrentMfaMethod(mfaProviders);
      const mfaMethodList = getMfaMethodsAvailable(mfaProviders);
      setMfaMethodsAvailable(mfaMethodList);
      setMfaMethod(currentMfaMethod);
    }
  }, [isLoading, mfaProviders]);

  // Error handling
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="p-6">
            <h2 className="text-destructive text-2xl font-bold">
              Authentication Error
            </h2>
            <p className="text-muted-foreground mt-2">
              Unable to load authentication methods. Please try again later.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (isLoading || !mfaMethod) {
    return <LoadingAnimate />;
  }

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden p-4">
      <Card className="relative z-10 w-full max-w-md">
        <CardContent>
          <div className="flex flex-col items-center justify-center">
            <CardHeader className="w-full">
              <CardTitle className="pt-2 text-center text-3xl font-bold">
                2-Step Verification
              </CardTitle>
            </CardHeader>
            <TwoFactorForm mfaMethod={mfaMethod} />
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <TwoFactorMethodSelector
            mfaMethodsAvailable={mfaMethodsAvailable}
            mfaMethod={mfaMethod}
            onChangeMfaMethod={setMfaMethod}
          />
          <Separator />
          <TwoFactorFooter />
        </CardFooter>
      </Card>
    </div>
  );
}
